import React, { useState, useEffect, useCallback } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Modal from '@mui/material/Modal';
import TextField from '@mui/material/TextField';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import { Plus, SquarePen, Trash2 } from 'lucide-react';
import {
    getAllAffiliateCommissions,
    createAffiliateCommission,
    updateAffiliateCommission,
    deleteAffiliateCommission
} from '../../Services/AffiliateInfo/AffiliateCommissionApi';
import PermissionWrapper from '../../Components/Auth/PermissionWrapper';
import DeleteModal from '../../Components/Modals/DeleteModal';
import { useGlobalSnackbar } from '../../Components/Common/GlobalSanckbar';

const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '-';
    return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
    });
};

const AffiliateCommission = () => {
    const { showSnackbar } = useGlobalSnackbar();
    const [commissions, setCommissions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [editingCommission, setEditingCommission] = useState(null);
    const [formData, setFormData] = useState({
        minBookings: '',
        maxBookings: '',
        percentage: '',
        isActive: true
    });

    const [modalError, setModalError] = useState(null);
    const [fieldErrors, setFieldErrors] = useState({});

    // Delete modal state
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [selectedId, setSelectedId] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState(null);

    const fetchCommissions = useCallback(async () => {
        try {
            setLoading(true);
            const response = await getAllAffiliateCommissions();
            if (response && response.success && response.data) {
                setCommissions(response.data);
            } else {
                setCommissions([]);
            }
        } catch (err) {
            console.error(err);
            setError("Failed to fetch affiliate commissions. Please try again later.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchCommissions();
    }, [fetchCommissions]);

    const handleOpenModal = (commission = null) => {
        setModalError(null);
        setFieldErrors({});
        setEditingCommission(commission);
        if (commission) {
            setFormData({
                minBookings: commission.minBookings,
                maxBookings: commission.maxBookings,
                percentage: commission.percentage,
                isActive: commission.isActive
            });
        } else {
            setFormData({
                minBookings: '',
                maxBookings: '',
                percentage: '',
                isActive: true
            });
        }
        setModalOpen(true);
    };

    const handleCloseModal = () => {
        setModalOpen(false);
        setEditingCommission(null);
        setModalError(null);
        setFieldErrors({});
        setFormData({
            minBookings: '',
            maxBookings: '',
            percentage: '',
            isActive: true
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setModalError(null);
        setFieldErrors({});

        const data = {
            minBookings: Number(formData.minBookings),
            maxBookings: Number(formData.maxBookings),
            percentage: Number(formData.percentage),
            isActive: formData.isActive
        };

        try {
            if (editingCommission) {
                await updateAffiliateCommission(editingCommission.id, data);
                showSnackbar("Affiliate commission updated successfully!", "success");
            } else {
                await createAffiliateCommission(data);
                showSnackbar("Affiliate commission created successfully!", "success");
            }
            fetchCommissions();
            handleCloseModal();
        } catch (err) {
            console.error("Error saving affiliate commission:", err);
            const serverError = err.response?.data;
            const errMsg = serverError?.errors?.[0] ||
                serverError?.details?.[0]?.message ||
                serverError?.message ||
                err.message ||
                "Failed to save affiliate commission.";

            setModalError(errMsg);
            showSnackbar(errMsg, "error");

            const errorsObj = {};
            if (serverError && serverError.details && Array.isArray(serverError.details)) {
                serverError.details.forEach(item => {
                    const fieldName = item.field === 'bookings' ? 'minBookings' : item.field;
                    errorsObj[fieldName] = item.message;
                });
                setFieldErrors(errorsObj);
            } else if (serverError && serverError.errors && Array.isArray(serverError.errors) && serverError.errors.length > 0) {
                if (serverError.errors[0].toLowerCase().includes("booking")) {
                    errorsObj.minBookings = serverError.errors[0];
                }
                setFieldErrors(errorsObj);
            }
        }
    };

    const handleDeleteClick = (id) => {
        setSelectedId(id);
        setDeleteError(null);
        setDeleteModalOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (selectedId) {
            try {
                setIsDeleting(true);
                setDeleteError(null);
                const response = await deleteAffiliateCommission(selectedId);
                if (response && response.success) {
                    setDeleteModalOpen(false);
                    setSelectedId(null);
                    showSnackbar("Affiliate commission deleted successfully!", "success");
                    fetchCommissions();
                } else {
                    setDeleteError(response?.message || "Failed to delete affiliate commission");
                }
            } catch (err) {
                console.error("Delete Error:", err);
                const errorMessage = err.response?.data?.message || err.message || "Failed to delete affiliate commission";
                setDeleteError(errorMessage);
            } finally {
                setIsDeleting(false);
            }
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        let newValue = type === 'checkbox' ? checked : value;

        if (name === 'percentage' && value !== '') {
            const num = Number(value);
            if (num > 100) {
                newValue = 100;
            } else if (num < 1) {
                newValue = 1;
            } else {
                newValue = value;
            }
        }

        setFormData(prev => ({
            ...prev,
            [name]: newValue
        }));

        if (fieldErrors[name]) {
            setFieldErrors(prev => ({
                ...prev,
                [name]: null
            }));
        }
        if (modalError) {
            setModalError(null);
        }
    };

    return (
        <PermissionWrapper permission="view">
            <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#FBFBFB', minHeight: '100vh' }}>
                {/* Header */}
                <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ mb: 4 }}
                >
                    <Typography
                        sx={{
                            color: '#222',
                            fontSize: { xs: '1.75rem', md: '1.8rem' },
                            fontWeight: 500
                        }}
                    >
                        Affiliate Commissions
                    </Typography>

                    <PermissionWrapper permission="create" mode="disable">
                        <Button
                            variant="contained"
                            startIcon={<Plus size={18} />}
                            onClick={() => handleOpenModal()}
                            disableElevation
                            sx={{ bgcolor: '#FFBD65', px: 3, color: '#fff', borderRadius: '8px' }}
                        >
                            Add Commission
                        </Button>
                    </PermissionWrapper>
                </Stack>

                {/* Error */}
                {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

                {/* Table */}
                <Paper
                    sx={{
                        borderRadius: "8px",
                        border: "1px solid #E8E8E8",
                        overflow: "hidden"
                    }}
                    elevation={0}
                >
                    <TableContainer>
                        <Table sx={{ minWidth: 1050 }}>
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#F8FAFC', borderBottom: '2px solid #EEF2F6' }}>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', textAlign: 'center' }}>S.No</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap' }}>Min Bookings</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap' }}>Max Bookings</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap' }}>Percentage</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap' }}>Active</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '130px' }}>Created By</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '130px' }}>Updated By</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '120px' }}>Created At</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '120px' }}>Updated At</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap' }}>Actions</TableCell>
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {loading ? (
                                    <TableRow>
                                        <TableCell colSpan={10} align="center" sx={{ py: 8 }}>
                                            <CircularProgress sx={{ color: '#FFBD65' }} />
                                        </TableCell>
                                    </TableRow>
                                ) : commissions.length > 0 ? (
                                    commissions.map((commission, idx) => (
                                        <TableRow key={commission.id} sx={{ '& td': { borderBottom: '1px solid #F0F0F0' }, '&:last-child td': { borderBottom: 0 } }}>
                                            <TableCell sx={{ textAlign: 'center', py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#666' }}>{idx + 1}</TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#1E293B', fontWeight: 500 }}>{commission.minBookings}</TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#1E293B' }}>{commission.maxBookings}</TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#1E293B', fontWeight: 600 }}>{commission.percentage}%</TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5 }}>
                                                <Box sx={{
                                                    display: 'inline-block',
                                                    px: 1.25,
                                                    py: 0.35,
                                                    borderRadius: '4px',
                                                    bgcolor: commission.isActive ? '#E8F5E8' : '#FFEBEE',
                                                    color: commission.isActive ? '#2E7D32' : '#C62828',
                                                    fontSize: '0.7rem',
                                                    fontWeight: 500
                                                }}>
                                                    {commission.isActive ? 'Yes' : 'No'}
                                                </Box>
                                            </TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                                {commission.cuName || '-'}
                                            </TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                                {commission.uuName || '-'}
                                            </TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                                {formatDate(commission.createdAt)}
                                            </TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                                {formatDate(commission.updatedAt)}
                                            </TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5 }}>
                                                <Stack direction="row" spacing={1}>
                                                    <PermissionWrapper permission="edit" mode="disable">
                                                        <Tooltip title="Edit">
                                                            <IconButton
                                                                size="small"
                                                                disableFocusRipple disableRipple disableTouchRipple
                                                                sx={{ color: '#444' }}
                                                                onClick={() => handleOpenModal(commission)}
                                                            >
                                                                <SquarePen size={18} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </PermissionWrapper>
                                                    <PermissionWrapper permission="delete" mode="disable">
                                                        <Tooltip title="Delete">
                                                            <IconButton
                                                                size="small"
                                                                disableFocusRipple disableRipple disableTouchRipple
                                                                sx={{ color: '#d32f2f' }}
                                                                onClick={() => handleDeleteClick(commission.id)}
                                                            >
                                                                <Trash2 size={18} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </PermissionWrapper>
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={10} align="center" sx={{ py: 3, color: '#64748B', fontSize: '0.8rem' }}>
                                            No affiliate commissions found.
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>

                {/* Modal */}
                <Modal open={modalOpen} onClose={handleCloseModal}>
                    <Box
                        sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            width: 400,
                            bgcolor: 'background.paper',
                            boxShadow: 24,
                            p: 4,
                            borderRadius: 2,
                        }}
                    >
                        <Typography variant="h6" component="h2" sx={{ mb: 2 }}>
                            {editingCommission ? 'Edit Affiliate Commission' : 'Add Affiliate Commission'}
                        </Typography>
                        {modalError && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                {modalError}
                            </Alert>
                        )}
                        <form onSubmit={handleSubmit}>
                            <TextField
                                fullWidth
                                label="Min Bookings"
                                name="minBookings"
                                type="number"
                                value={formData.minBookings}
                                onChange={handleInputChange}
                                required
                                error={Boolean(fieldErrors.minBookings || fieldErrors.bookings)}
                                helperText={fieldErrors.minBookings || fieldErrors.bookings}
                                sx={{
                                    mb: 2,
                                    '& input[type=number]': {
                                        '-moz-appearance': 'textfield',
                                    },
                                    '& input[type=number]::-webkit-outer-spin-button': {
                                        '-webkit-appearance': 'none',
                                        margin: 0,
                                    },
                                    '& input[type=number]::-webkit-inner-spin-button': {
                                        '-webkit-appearance': 'none',
                                        margin: 0,
                                    },
                                }}
                            />
                            <TextField
                                fullWidth
                                label="Max Bookings"
                                name="maxBookings"
                                type="number"
                                value={formData.maxBookings}
                                onChange={handleInputChange}
                                required
                                error={Boolean(fieldErrors.maxBookings)}
                                helperText={fieldErrors.maxBookings}
                                sx={{
                                    mb: 2,
                                    '& input[type=number]': {
                                        '-moz-appearance': 'textfield',
                                    },
                                    '& input[type=number]::-webkit-outer-spin-button': {
                                        '-webkit-appearance': 'none',
                                        margin: 0,
                                    },
                                    '& input[type=number]::-webkit-inner-spin-button': {
                                        '-webkit-appearance': 'none',
                                        margin: 0,
                                    },
                                }}
                            />
                            <TextField
                                fullWidth
                                label="Percentage"
                                name="percentage"
                                type="number"
                                value={formData.percentage}
                                onChange={handleInputChange}
                                required
                                error={Boolean(fieldErrors.percentage)}
                                helperText={fieldErrors.percentage}
                                inputProps={{ min: 1, max: 100 }}
                                sx={{
                                    mb: 2,
                                    '& input[type=number]': {
                                        '-moz-appearance': 'textfield',
                                    },
                                    '& input[type=number]::-webkit-outer-spin-button': {
                                        '-webkit-appearance': 'none',
                                        margin: 0,
                                    },
                                    '& input[type=number]::-webkit-inner-spin-button': {
                                        '-webkit-appearance': 'none',
                                        margin: 0,
                                    },
                                }}
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={formData.isActive}
                                        onChange={handleInputChange}
                                        name="isActive"
                                    />
                                }
                                label="Active"
                                sx={{ mb: 2 }}
                            />
                            <Stack direction="row" spacing={2}>
                                <Button type="submit" variant="contained" sx={{ bgcolor: '#FFBD65' }}>
                                    {editingCommission ? 'Update' : 'Create'}
                                </Button>
                                <Button variant="outlined" onClick={handleCloseModal}>
                                    Cancel
                                </Button>
                            </Stack>
                        </form>
                    </Box>
                </Modal>
                <DeleteModal
                    open={deleteModalOpen}
                    onClose={() => setDeleteModalOpen(false)}
                    onDelete={handleConfirmDelete}
                    title="Delete Affiliate Commission"
                    description="Are you sure you want to delete this affiliate commission? This action cannot be undone."
                    error={deleteError}
                    loading={isDeleting}
                />
            </Box>
        </PermissionWrapper>
    );
};

export default AffiliateCommission;